import {
  describeBatteryPricingError,
  isBatteryComputeNotPricedError,
} from "~/api/battery-pricing-errors"
import type { BatteryArbitrageDto } from "~/api/dto/battery-arbitrage.dto"
import type {
  BatteryFinancingSummaryDto,
  BatteryProposalArbitrageDto,
  BatteryProposalFinancialSummaryResponseDto,
} from "~/api/dto/battery-proposal.dto"
import { savingsAreShown } from "~/api/hooks/useBatteryProposalArbitrage"

import { batteryArbitrageReasonCopy } from "./setup/battery-arbitrage-status"
import {
  batterySetupStepPath,
  SETUP_STEP_LABELS,
  type BatterySetupStep,
} from "./setup/setup-steps"
import { describePricingError } from "../ProposalPresentation/pricing/refresh-pricing-action"
import type {
  UpdateCopy,
  UpdateFix,
  UpdatesSpec,
} from "../ProposalPresentation/updates/proposal-updates"
import type { UpdateStop } from "../ProposalPresentation/updates/use-updates-runner"

/**
 * What can be out of date on a Battery-Only Proposal, in the order a fix runs.
 * The payment is quoted on the price, so pricing runs first. Savings model the
 * equipment, usage and rate plan, never the price, so they run on their own,
 * ahead of a LightReach payment the panel can only link to: Update all fixes
 * everything it can before it stops there.
 */
export const BATTERY_UPDATE_ORDER = [`pricing`, `savings`, `payment`] as const
export type BatteryUpdateKey = (typeof BATTERY_UPDATE_ORDER)[number]

export const BATTERY_UPDATE_DEPENDENCIES: Record<
  BatteryUpdateKey,
  readonly BatteryUpdateKey[]
> = {
  pricing: [],
  payment: [`pricing`],
  savings: [],
}

export const BATTERY_UPDATE_COPY: Record<BatteryUpdateKey, UpdateCopy> = {
  pricing: {
    name: `Pricing`,
    stale: `Out of date`,
    busy: `Refreshing pricing`,
    action: `Refresh`,
  },
  payment: {
    name: `Payment`,
    stale: `Needs a current LightReach offer`,
    busy: `Quoting the payment`,
    action: `Choose offer`,
  },
  savings: {
    name: `Savings`,
    stale: `Out of date`,
    busy: `Calculating savings`,
    action: `Recalculate`,
    plural: true,
  },
}

/**
 * The section each update's row scrolls to, and the rail dots it badges.
 * Savings that aren't current aren't shown, so their row falls back to the top.
 */
export const BATTERY_UPDATE_SECTIONS: Record<
  BatteryUpdateKey,
  readonly string[]
> = {
  pricing: [`investment`],
  payment: [`investment`],
  savings: [`savings`, `hero`],
}

/** The Battery-Only Proposal's Updates panel: what it lists, in what order, and where. */
export const BATTERY_UPDATES_SPEC: UpdatesSpec<BatteryUpdateKey> = {
  order: BATTERY_UPDATE_ORDER,
  dependencies: BATTERY_UPDATE_DEPENDENCIES,
  copy: BATTERY_UPDATE_COPY,
  sections: BATTERY_UPDATE_SECTIONS,
  idPrefix: `battery`,
}

/** The reads that say what's out of date; undefined while one is loading. */
export type BatteryUpdateReads = {
  readonly pricing:
    | (Pick<BatteryProposalFinancialSummaryResponseDto, `pricingStatus`> & {
        readonly financingSummary?: Pick<
          BatteryFinancingSummaryDto,
          `status`
        > | null
      })
    | undefined
  /** The Proposal read's copy of the savings lifecycle. */
  readonly savings:
    | Pick<
        BatteryProposalArbitrageDto,
        `failureReason` | `status` | `visibility`
      >
    | undefined
  /**
   * The reads whose last check failed, by the update each answers for
   * (pricing and payment share the financial summary's). Whether they are
   * current is unknown.
   */
  readonly unknown?: ReadonlySet<BatteryUpdateKey>
  /**
   * A LightReach Energy Backup plan: Refresh Pricing prices the battery, but
   * only saving an offer again quotes its payment.
   */
  readonly priceSetByOffer?: boolean
  /** Savings are calculated for this deployment at all. */
  readonly savingsEnabled: boolean
  /**
   * The panel started the savings run the read describes, so its row stays
   * while the queue works and says how the run ended, even where the panel
   * wouldn't list that state on its own.
   */
  readonly savingsTracked?: boolean
}

/** Savings statuses the panel can fix: a superseded run, or a failed one. */
const SAVINGS_TO_FIX: ReadonlySet<string> = new Set([`outdated`, `failed`])

/** A run on its way through the queue. */
const SAVINGS_IN_FLIGHT: ReadonlySet<string> = new Set([`pending`, `running`])

/**
 * Whether one update is needed, by its own read. A read still loading isn't.
 *
 * Refresh Pricing re-quotes a Concert, Sungage or Credit Human payment with
 * the price, so pricing answers for a stale payment while the price itself is
 * out of date. A payment that stays stale on a current price (a LightReach
 * offer, or a Loan Product no longer offered) is quoted again on Loan Product.
 */
export const isBatteryUpdateNeeded = (
  key: BatteryUpdateKey,
  {
    pricing,
    savings,
    priceSetByOffer = false,
    savingsEnabled,
    savingsTracked = false,
  }: BatteryUpdateReads,
): boolean => {
  switch (key) {
    case `pricing`:
      return pricing != null && pricing.pricingStatus !== `current`
    case `payment`:
      return (
        pricing != null &&
        pricing.financingSummary?.status === `stale` &&
        (priceSetByOffer || pricing.pricingStatus === `current`)
      )
    case `savings`: {
      // An unavailable or never-calculated run needs an input, which Setup
      // names; a pending or running one is already on its way. The panel's
      // own run stays listed until it ends current.
      if (
        !savingsEnabled ||
        savings == null ||
        !savingsAreShown(savings.visibility) ||
        savings.status == null
      ) {
        return false
      }
      return (
        SAVINGS_TO_FIX.has(savings.status) ||
        (savingsTracked &&
          (SAVINGS_IN_FLIGHT.has(savings.status) ||
            savings.status === `unavailable`))
      )
    }
  }
}

/** The updates a proposal needs, in fix order. */
export const batteryUpdatesNeeded = (
  reads: BatteryUpdateReads,
): readonly BatteryUpdateKey[] =>
  BATTERY_UPDATE_ORDER.filter((key) => isBatteryUpdateNeeded(key, reads))

/** A savings run still in the queue: nothing for the panel to start. */
export const batterySavingsInFlight = ({
  savings,
}: Pick<BatteryUpdateReads, `savings`>): boolean =>
  savings?.status != null && SAVINGS_IN_FLIGHT.has(savings.status)

/** Why a calculation failed, in the Setup rail's words. */
const savingsFailureCopy = (
  reason: string | null,
): ReturnType<typeof batteryArbitrageReasonCopy> =>
  batteryArbitrageReasonCopy(
    reason,
    batteryArbitrageReasonCopy(`provider_calculation_failed`),
  )

/**
 * A row's state while it waits: never calculated, out of date, still in the
 * queue, or why a calculation didn't finish.
 */
export const batteryUpdateStaleLabel = (
  key: BatteryUpdateKey,
  { pricing, savings, priceSetByOffer = false }: BatteryUpdateReads,
): string => {
  switch (key) {
    case `pricing`:
      return pricing?.pricingStatus === `not_calculated`
        ? `Not calculated yet`
        : BATTERY_UPDATE_COPY.pricing.stale
    case `payment`:
      return priceSetByOffer
        ? BATTERY_UPDATE_COPY.payment.stale
        : `Needs a current quote`
    case `savings`:
      return savings?.status === `failed`
        ? savingsFailureCopy(savings.failureReason).headline
        : savings?.status === `unavailable`
          ? batteryArbitrageReasonCopy(savings.failureReason).headline
          : batterySavingsInFlight({ savings })
            ? `Still calculating`
            : BATTERY_UPDATE_COPY.savings.stale
  }
}

/** A row's button: a failed calculation is retried, not recalculated. */
export const batteryUpdateActionLabel = (
  key: BatteryUpdateKey,
  { savings }: Pick<BatteryUpdateReads, `savings`>,
): string =>
  key === `savings` && savings?.status === `failed`
    ? `Retry`
    : BATTERY_UPDATE_COPY[key].action

const setupStepFix = (
  proposalId: string,
  step: BatterySetupStep,
): UpdateFix => ({
  path: batterySetupStepPath(proposalId, step),
  label: `Open ${SETUP_STEP_LABELS[step]}`,
})

/**
 * Where a payment is quoted again: a current LightReach offer, or the Loan
 * Product, both on the Loan Product step.
 */
export const batteryPaymentFix = (
  proposalId: string,
  priceSetByOffer: boolean,
): UpdateFix =>
  priceSetByOffer
    ? {
        path: batterySetupStepPath(proposalId, `finance-product`),
        label: BATTERY_UPDATE_COPY.payment.action,
      }
    : setupStepFix(proposalId, `finance-product`)

/** Where the input a calculation needs is supplied. */
export const batterySavingsFix = (
  proposalId: string,
  { savings }: Pick<BatteryUpdateReads, `savings`>,
): UpdateFix | null => {
  if (savings?.status !== `unavailable`) return null
  const { fixStep } = batteryArbitrageReasonCopy(savings.failureReason)
  return setupStepFix(proposalId, fixStep ?? `arbitrage`)
}

/** What stops an Update all that reaches a payment the panel can't quote. */
export const BATTERY_LIGHTREACH_PAYMENT_UPDATE_MESSAGE = `Save a current LightReach offer to quote the payment.`
export const BATTERY_PAYMENT_UPDATE_MESSAGE = `Choose the Loan Product again to quote the payment.`

export const SAVINGS_START_ERROR_PROMPT = `Unable to start another calculation. Try again in a moment.`
export const SAVINGS_STILL_CALCULATING_MESSAGE = `Savings are still calculating. Reload in a moment to see them.`
export const SAVINGS_CHECK_ERROR_MESSAGE = `Couldn't check on the calculation. It keeps running; reload in a moment to see it.`
export const SAVINGS_INPUTS_CHANGED_MESSAGE = `The inputs changed while savings calculated. Recalculate to include them.`

/**
 * A savings calculation the panel started and waited for, which didn't end
 * current: it failed, needs an input, went out of date, or outlasted the wait.
 */
export class BatterySavingsStop extends Error {
  public readonly fix: UpdateFix | null

  public constructor(message: string, fix: UpdateFix | null = null) {
    super(message)
    this.name = `BatterySavingsStop`
    this.fix = fix
  }
}

/** Why a finished calculation isn't current, in the Setup rail's words. */
export const batterySavingsStop = (
  arbitrage: Pick<BatteryArbitrageDto, `reason` | `status`>,
  proposalId: string,
): BatterySavingsStop | null => {
  if (arbitrage.status === `outdated`) {
    return new BatterySavingsStop(SAVINGS_INPUTS_CHANGED_MESSAGE)
  }
  if (arbitrage.status !== `failed` && arbitrage.status !== `unavailable`) {
    return null
  }
  const copy =
    arbitrage.status === `failed`
      ? savingsFailureCopy(arbitrage.reason)
      : batteryArbitrageReasonCopy(arbitrage.reason)
  return new BatterySavingsStop(
    `${copy.headline}.`,
    copy.fixStep == null ? null : setupStepFix(proposalId, copy.fixStep),
  )
}

/** Each update's failure, in the copy its inline action uses. */
export const describeBatteryUpdateError = ({
  key,
  error,
  proposalId,
}: {
  key: BatteryUpdateKey
  error: unknown
  proposalId: string
}): UpdateStop => {
  if (key === `savings`) {
    return error instanceof BatterySavingsStop
      ? { message: error.message, fix: error.fix }
      : { message: SAVINGS_START_ERROR_PROMPT, fix: null }
  }
  return {
    message: describeBatteryPricingError(error, describePricingError(error)),
    // PowurOS priced without the AI compute node, which is equipment: the
    // node is the fix.
    fix: isBatteryComputeNotPricedError(error)
      ? setupStepFix(proposalId, `equipment`)
      : null,
  }
}
